import React, { useEffect, useState } from 'react';
import { KeyRound, X, Eye, EyeOff } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import Button from '../ui/Button';
import Input from '../ui/Input';
import { apiClient } from '../../services/api';

export type AdminPasswordTarget =
  | { kind: 'player'; userId: string }
  | { kind: 'shop'; agentId: string; shopId: string }
  | { kind: 'cashier'; cashierId: string };

export type AdminSetPasswordModalProps = {
  open: boolean;
  onClose: () => void;
  target: AdminPasswordTarget;
  minLength?: number;
  title?: string;
  onSuccess?: (data?: unknown) => void;
};

function extractError(err: unknown): string {
  const anyErr = err as { message?: string; response?: { data?: { message?: string } } };
  return anyErr?.response?.data?.message || anyErr?.message || 'Failed to update password';
}

async function submitPassword(
  target: AdminPasswordTarget,
  body: { password: string; reason: string; mustChangePassword?: boolean }
) {
  switch (target.kind) {
    case 'player':
      return apiClient.patchAdminUserPassword(target.userId, body);
    case 'shop':
      return apiClient.patchAdminShopPassword(target.agentId, target.shopId, body);
    case 'cashier':
      return apiClient.postAdminCashierResetPassword(target.cashierId, body);
    default:
      throw new Error('Unknown password target');
  }
}

const AdminSetPasswordModal: React.FC<AdminSetPasswordModalProps> = ({
  open,
  onClose,
  target,
  minLength = 8,
  title = 'Set password',
  onSuccess,
}) => {
  const [password, setPasswordValue] = useState('');
  const [confirm, setConfirm] = useState('');
  const [reason, setReason] = useState('');
  const [mustChangePassword, setMustChangePassword] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setPasswordValue('');
    setConfirm('');
    setReason('');
    setMustChangePassword(false);
    setShowPassword(false);
    setError(null);
  }, [open]);

  if (!open) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    if (password.length < minLength) {
      setError(`Password must be at least ${minLength} characters`);
      return;
    }
    if (password !== confirm) {
      setError('Passwords do not match');
      return;
    }
    if (reason.trim().length < 3) {
      setError('Reason is required (min 3 characters)');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const res = await submitPassword(target, {
        password,
        reason: reason.trim(),
        ...(target.kind === 'player' ? { mustChangePassword } : {}),
      });
      onSuccess?.(res);
      onClose();
    } catch (err) {
      setError(extractError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 !mt-0 p-0 sm:p-4"
      role="dialog"
      aria-modal="true"
      onClick={(e) => {
        if (e.target === e.currentTarget && !submitting) onClose();
      }}
    >
      <Card
        className="w-full sm:max-w-md max-h-[92vh] overflow-y-auto rounded-b-none sm:rounded-lg shadow-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <CardHeader className="space-y-2 pb-2">
          <div className="flex items-start justify-between gap-2">
            <CardTitle className="text-base sm:text-lg flex items-center gap-2 min-w-0 pr-2">
              <KeyRound className="h-5 w-5 shrink-0" />
              <span className="break-words">{title}</span>
            </CardTitle>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="shrink-0"
              onClick={onClose}
              disabled={submitting}
              aria-label="Close"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
          <p className="text-xs sm:text-sm text-muted-foreground font-normal">
            All existing sessions for this account will be signed out.
          </p>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="text-sm font-medium" htmlFor="admin-set-password">
                New password
              </label>
              <div className="relative mt-1">
                <Input
                  id="admin-set-password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  value={password}
                  onChange={(ev) => setPasswordValue(ev.target.value)}
                  disabled={submitting}
                />
                <button
                  type="button"
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground p-1"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            <div>
              <label className="text-sm font-medium" htmlFor="admin-set-password-confirm">
                Confirm password
              </label>
              <Input
                id="admin-set-password-confirm"
                className="mt-1"
                type={showPassword ? 'text' : 'password'}
                autoComplete="new-password"
                value={confirm}
                onChange={(ev) => setConfirm(ev.target.value)}
                disabled={submitting}
              />
            </div>
            {target.kind === 'player' ? (
              <label className="flex items-start gap-2 text-sm">
                <input
                  type="checkbox"
                  className="mt-1 shrink-0"
                  checked={mustChangePassword}
                  onChange={(ev) => setMustChangePassword(ev.target.checked)}
                  disabled={submitting}
                />
                <span>Require player to change password on next login</span>
              </label>
            ) : null}
            <div>
              <label className="text-sm font-medium" htmlFor="admin-set-password-reason">
                Reason
              </label>
              <Input
                id="admin-set-password-reason"
                className="mt-1"
                value={reason}
                onChange={(ev) => setReason(ev.target.value)}
                placeholder="Why are you resetting this password?"
                disabled={submitting}
              />
            </div>
            {error ? <p className="text-sm text-destructive break-words">{error}</p> : null}
            <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end pt-1">
              <Button type="button" variant="outline" className="w-full sm:w-auto" onClick={onClose} disabled={submitting}>
                Cancel
              </Button>
              <Button type="submit" className="w-full sm:w-auto" disabled={submitting}>
                {submitting ? 'Updating…' : 'Update password'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminSetPasswordModal;
